import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Autoplay from "embla-carousel-autoplay";
import {
  Linkedin,
  ArrowUpRight,
  Sparkles,
  Plus,
  Loader2,
  Pause,
  Play,
  RotateCcw,
} from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Reveal } from "@/components/Reveal";
import { SOCIAL } from "@/lib/site";
import { postsFromUrls, type LinkedInPost } from "@/lib/linkedin-posts";
import { LINKEDIN_POST_URLS, MAX_POSTS } from "@/config/linkedin";
import { cn } from "@/lib/utils";

// Post URLs + feed size live in one shared config file: src/config/linkedin.ts
const LINKEDIN_POSTS: LinkedInPost[] = postsFromUrls(LINKEDIN_POST_URLS).slice(0, MAX_POSTS);


type Props = {
  eyebrow?: string;
  heading?: string;
  description?: string;
  initialCount?: number;
  step?: number;
};

const STORAGE_KEY = "linkedin-feed:visibleCount";


export function LinkedInCarousel({
  eyebrow = "From LinkedIn",
  heading = "Field notes & perspectives",
  description = "Selected posts on security infrastructure, smart automation, luxury cinema and the projects shaping intelligent environments.",
  initialCount = MAX_POSTS,
  step = MAX_POSTS,

}: Props) {
  const total = LINKEDIN_POSTS.length;
  const [visibleCount, setVisibleCount] = useState(() => Math.min(initialCount, total));
  const [pendingCount, setPendingCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hydratedRef = useRef(false);

  // Hydrate visibleCount from localStorage (after mount to avoid SSR mismatch).
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = parseInt(raw, 10);
        if (Number.isFinite(parsed)) {
          const clamped = Math.min(Math.max(parsed, initialCount), total);
          if (clamped !== visibleCount) setVisibleCount(clamped);
        }
      }
    } catch {
      /* ignore */
    }
    hydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist visibleCount.
  useEffect(() => {
    if (!hydratedRef.current || typeof window === "undefined") return;
    try {
      if (visibleCount <= initialCount) {
        window.localStorage.removeItem(STORAGE_KEY);
      } else {
        window.localStorage.setItem(STORAGE_KEY, String(visibleCount));
      }
    } catch {
      /* ignore */
    }
  }, [visibleCount, initialCount]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const visiblePosts = useMemo(() => LINKEDIN_POSTS.slice(0, visibleCount), [visibleCount]);

  if (!total) return null;

  const remaining = total - visibleCount - pendingCount;

  const handleLoadMore = () => {
    if (isLoading) return;
    const next = Math.min(step, total - visibleCount);
    if (next <= 0) return;
    setIsLoading(true);
    setPendingCount(next);
    timerRef.current = setTimeout(() => {
      setVisibleCount((c) => Math.min(c + next, total));
      setPendingCount(0);
      setIsLoading(false);
    }, 450);
  };

  const handleReset = () => {
    setVisibleCount(Math.min(initialCount, total));
  };

  return (
    <section
      className="relative py-24 md:py-32 border-t border-border bg-surface overflow-hidden"
      aria-busy={isLoading}
    >
      <div className="absolute inset-0 grain pointer-events-none" />
      <div
        className="absolute -bottom-40 -left-40 h-[420px] w-[420px] rounded-full blur-3xl opacity-25 pointer-events-none"
        style={{
          background: "radial-gradient(circle, hsl(var(--primary) / 0.30), transparent 70%)",
        }}
      />

      <div className="container-page relative">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
            <div className="max-w-2xl">
              <span className="eyebrow">{eyebrow}</span>
              <h2
                className="mt-4 text-balance font-semibold tracking-tight leading-[1.05]"
                style={{ fontSize: "clamp(1.75rem, 5vw, 3rem)" }}
              >
                {heading}
              </h2>
              <p
                className="mt-5 text-pretty text-ink-soft leading-relaxed"
                style={{ fontSize: "clamp(0.95rem, 1.7vw, 1.125rem)" }}
              >
                {description}
              </p>
            </div>
            <a
              href={SOCIAL.linkedin}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 self-start md:self-auto rounded-full border border-border bg-background/60 px-5 py-3 text-sm font-medium hover:border-primary hover:text-primary transition-colors backdrop-blur-md"
            >
              <Linkedin className="h-4 w-4" />
              Follow on LinkedIn
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <PostsCarousel key={visibleCount} posts={visiblePosts} pendingCount={pendingCount} />
        </Reveal>

        {(remaining > 0 || isLoading || visibleCount > initialCount) && (
          <Reveal delay={0.1}>
            <div className="mt-12 flex flex-col items-center gap-3">
              {(remaining > 0 || isLoading) && (
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={isLoading || remaining <= 0}
                  aria-label="Load more LinkedIn posts"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground hover:opacity-90 transition disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading…
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      Load more posts
                    </>
                  )}
                </button>
              )}
              <div className="flex items-center gap-3 text-xs text-ink-soft">
                <p role="status" aria-live="polite" aria-atomic="true">
                  Showing {visibleCount} of {total}
                  <span className="sr-only">
                    {remaining > 0
                      ? `. ${remaining} more post${remaining === 1 ? "" : "s"} available.`
                      : ". All posts loaded."}
                  </span>
                </p>
                {visibleCount > initialCount && (
                  <>
                    <span aria-hidden="true">·</span>
                    <button
                      type="button"
                      onClick={handleReset}
                      className="inline-flex items-center gap-1 underline-offset-4 hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                    >
                      <RotateCcw className="h-3 w-3" />
                      Reset
                    </button>
                  </>
                )}
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}

function PostsCarousel({ posts, pendingCount }: { posts: LinkedInPost[]; pendingCount: number }) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const autoplay = useRef(
    Autoplay({
      delay: 6000,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
      stopOnFocusIn: true,
    }),
  );

  const [api, setApi] = useState<CarouselApi>();
  const [snapCount, setSnapCount] = useState(0);
  const [selectedSnap, setSelectedSnap] = useState(0);
  const [isPlaying, setIsPlaying] = useState(!prefersReducedMotion);
  const [highlightFirst, setHighlightFirst] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasAutoScrolledRef = useRef(false);

  // When the carousel scrolls into view for the first time, snap to the first
  // post and briefly highlight it so visitors see the featured card.
  useEffect(() => {
    if (!api) return;
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !hasAutoScrolledRef.current) {
            hasAutoScrolledRef.current = true;
            api.scrollTo(0, prefersReducedMotion);
            setHighlightFirst(true);
            window.setTimeout(() => setHighlightFirst(false), 1800);
          }
        }
      },
      { threshold: 0.35 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [api, prefersReducedMotion]);


  useEffect(() => {
    if (!api) return;
    const update = () => {
      setSnapCount(api.scrollSnapList().length);
      setSelectedSnap(api.selectedScrollSnap());
    };
    update();
    api.on("select", update);
    api.on("reInit", update);
    return () => {
      api.off("select", update);
      api.off("reInit", update);
    };
  }, [api]);

  // Respect reduced motion: stop autoplay if user prefers reduced motion.
  useEffect(() => {
    const ap = autoplay.current;
    if (!ap) return;
    if (prefersReducedMotion) {
      ap.stop();
      setIsPlaying(false);
    }
  }, [prefersReducedMotion]);

  const toggleAutoplay = useCallback(() => {
    const ap = autoplay.current;
    if (!ap) return;
    if (isPlaying) {
      ap.stop();
      setIsPlaying(false);
    } else {
      ap.play();
      setIsPlaying(true);
    }
  }, [isPlaying]);

  return (
    <div ref={containerRef} className="relative">
      <Carousel
        setApi={setApi}
        opts={{
          align: "start",
          loop: true,
          slidesToScroll: "auto",
          containScroll: "trimSnaps",
          dragThreshold: 10,
          duration: 25,
        }}
        plugins={[autoplay.current]}
        aria-label="LinkedIn posts carousel"
        className="w-full rounded-3xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <CarouselContent className="-ml-4 touch-pan-y">
          {posts.map((post, i) => (
            <CarouselItem
              key={`${post.embedUrl}-${i}`}
              aria-roledescription="slide"
              aria-label={`Post ${i + 1} of ${posts.length}`}
              className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3 animate-in fade-in slide-in-from-bottom-2 duration-500"
            >
              <PostCard post={post} index={i} highlight={i === 0 && highlightFirst} />
            </CarouselItem>
          ))}
          {Array.from({ length: pendingCount }).map((_, i) => (
            <CarouselItem
              key={`skeleton-${i}`}
              aria-hidden="true"
              className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3"
            >
              <PostCardSkeleton />
            </CarouselItem>
          ))}
        </CarouselContent>

        <CarouselPrevious
          aria-label="Previous post"
          className="tap-target absolute left-2 md:-left-5 top-1/2 -translate-y-1/2 z-10 h-10 w-10 bg-background/85 backdrop-blur-md border-border shadow-lg hover:bg-background"
        />
        <CarouselNext
          aria-label="Next post"
          className="tap-target absolute right-2 md:-right-5 top-1/2 -translate-y-1/2 z-10 h-10 w-10 bg-background/85 backdrop-blur-md border-border shadow-lg hover:bg-background"
        />
      </Carousel>

      {snapCount > 1 && (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-3 py-1 text-xs font-medium text-ink-soft backdrop-blur-md"
          >
            Page {selectedSnap + 1} / {snapCount}
          </div>

          <div className="flex items-center gap-1.5" role="tablist" aria-label="Carousel pages">
            {Array.from({ length: Math.min(snapCount, 8) }).map((_, i) => {
              const isActive = i === selectedSnap;
              return (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  // 44x44 hit area on touch, dot stays visually small.
                  aria-label={`Go to page ${i + 1}`}
                  aria-selected={isActive}
                  onClick={() => api?.scrollTo(i)}
                  className={cn(
                    "tap-target h-2 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                    isActive ? "w-6 bg-primary" : "w-2 bg-border hover:bg-foreground/40",
                  )}
                />
              );
            })}
          </div>

          <button
            type="button"
            onClick={toggleAutoplay}
            aria-label={isPlaying ? "Pause autoplay" : "Play autoplay"}
            aria-pressed={isPlaying}
            className="tap-target inline-flex items-center gap-1.5 rounded-full border border-border bg-background/60 px-3 py-1 text-xs font-medium text-ink-soft hover:border-primary hover:text-primary transition-colors backdrop-blur-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            {isPlaying ? (
              <>
                <Pause className="h-3 w-3" />
                Pause
              </>
            ) : (
              <>
                <Play className="h-3 w-3" />
                Play
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

function usePrefersReducedMotion() {
  const [prefers, setPrefers] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefers(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefers(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return prefers;
}

function PostCard({
  post,
  index,
  highlight = false,
}: {
  post: LinkedInPost;
  index: number;
  highlight?: boolean;
}) {
  const height = post.height ?? 600;
  const prefersReducedMotion = usePrefersReducedMotion();

  // Only mount the LinkedIn iframe once the card is near the viewport.
  const frameRef = useRef<HTMLDivElement>(null);
  const [shouldLoadFrame, setShouldLoadFrame] = useState(false);

  useEffect(() => {
    if (shouldLoadFrame) return;
    const node = frameRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setShouldLoadFrame(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShouldLoadFrame(true);
          observer.disconnect();
        }
      },
      { rootMargin: "400px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [shouldLoadFrame]);


  const captionRef = useRef<HTMLSpanElement>(null);
  const summaryRef = useRef<HTMLParagraphElement>(null);
  const [overflow, setOverflow] = useState<{
    caption: boolean;
    summary: boolean;
  }>({ caption: false, summary: false });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const measure = () => {
      const cap = captionRef.current;
      const sum = summaryRef.current;
      const captionClamped = !!cap && cap.scrollWidth > cap.clientWidth + 1;
      const summaryClamped = !!sum && sum.scrollHeight > sum.clientHeight + 1;
      setOverflow((prev) =>
        prev.caption === captionClamped && prev.summary === summaryClamped
          ? prev
          : { caption: captionClamped, summary: summaryClamped },
      );
      if (import.meta.env.DEV && (captionClamped || summaryClamped)) {
        console.debug(`[LinkedInCarousel] Card #${index + 1} clamped:`, {
          caption: captionClamped,
          summary: summaryClamped,
          captionText: post.caption,
          summaryPreview: post.summary?.slice(0, 60),
        });
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (captionRef.current) ro.observe(captionRef.current);
    if (summaryRef.current) ro.observe(summaryRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [index, post.caption, post.summary]);

  const isClamped = overflow.caption || overflow.summary;
  const motionTransition = prefersReducedMotion ? "transition-none" : "transition-colors";

  return (
    <div
      className={cn(
        "group relative flex h-full flex-col overflow-hidden rounded-3xl border border-border/70 bg-surface/40 backdrop-blur-xl shadow-[0_30px_80px_-40px_rgba(0,0,0,0.35)] hover:border-primary/60",
        motionTransition,
        highlight &&
          !prefersReducedMotion &&
          "ring-2 ring-primary/70 ring-offset-2 ring-offset-background border-primary/70 shadow-[0_30px_80px_-30px_hsl(var(--primary)/0.45)] [animation:pulse_1.4s_ease-in-out_1]",
        highlight && prefersReducedMotion && "ring-2 ring-primary/70 border-primary/70",
      )}
    >
      {import.meta.env.DEV && isClamped && (
        <div
          aria-hidden="true"
          title={`Clamped: ${overflow.caption ? "caption " : ""}${overflow.summary ? "summary" : ""}`}
          className="absolute right-2 top-2 z-20 inline-flex items-center gap-1 rounded-full bg-amber-500/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-black shadow-md"
        >
          ✂︎ clamp
        </div>
      )}

      {(post.caption || post.summary) && (
        <div
          className="flex items-center justify-between gap-3"
          style={{
            paddingInline: "clamp(0.875rem, 2.5vw, 1.25rem)",
            paddingTop: "clamp(0.875rem, 2.5vw, 1.25rem)",
          }}
        >
          <span
            ref={captionRef}
            className="eyebrow text-ink-soft leading-snug line-clamp-1 min-w-0 flex-1"
            style={{ fontSize: "clamp(0.65rem, 1.6vw, 0.75rem)" }}
            title={post.caption ?? "From LinkedIn"}
          >
            {post.caption ?? "From LinkedIn"}
          </span>
          <Linkedin className="h-4 w-4 shrink-0 text-primary/70" />
        </div>
      )}

      {post.summary && (
        <div
          className="rounded-2xl border border-border/60 bg-background/60"
          style={{
            marginInline: "clamp(0.875rem, 2.5vw, 1.25rem)",
            marginTop: "clamp(0.625rem, 1.5vw, 0.75rem)",
            padding: "clamp(0.75rem, 2vw, 1rem)",
          }}
        >
          <div
            className="mb-2 inline-flex items-center gap-1.5 font-semibold uppercase tracking-widest text-primary/80"
            style={{ fontSize: "clamp(0.6rem, 1.4vw, 0.7rem)" }}
          >
            <Sparkles className="h-3 w-3" />
            AI summary
          </div>
          <p
            ref={summaryRef}
            className="text-pretty leading-relaxed text-foreground/90 line-clamp-3 sm:line-clamp-4 break-words [overflow-wrap:anywhere]"
            style={{
              fontSize: "clamp(0.8125rem, 1.7vw, 0.9rem)",
              lineHeight: 1.55,
            }}
            title={post.summary}
          >
            {post.summary}
          </p>
        </div>
      )}

      <div className="mt-auto" style={{ padding: "clamp(0.625rem, 1.5vw, 0.75rem)" }}>
        <div
          ref={frameRef}
          className="relative w-full overflow-hidden rounded-2xl border border-border/60 bg-background"
          style={{
            height: `clamp(440px, ${Math.round(height * 0.75)}px, ${height}px)`,
          }}
        >
          {shouldLoadFrame ? (
            <iframe
              src={post.embedUrl}
              title={post.summary ?? "LinkedIn post by Arif Khan"}
              loading="lazy"
              allow="encrypted-media"
              className="absolute inset-0 h-full w-full pointer-events-none"
              frameBorder={0}
            />
          ) : (
            <div
              aria-hidden="true"
              className="absolute inset-0 flex items-center justify-center bg-muted/20"
            >
              <Linkedin className="h-6 w-6 text-primary/40" />
            </div>
          )}
        </div>


        <a
          href={getPublicPostUrl(post.embedUrl)}
          target="_blank"
          rel="noopener noreferrer"
          className="tap-target mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full border border-border bg-background/70 px-4 py-2.5 text-xs font-medium text-foreground hover:border-primary hover:text-primary backdrop-blur-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background transition-colors"
        >
          <Linkedin className="h-3.5 w-3.5" />
          Open on LinkedIn →
        </a>

      </div>
    </div>
  );
}

function getPublicPostUrl(embedUrl: string): string {
  // Convert "/embed/feed/update/urn:..." → "/feed/update/urn:..." and strip
  // any embed-specific query params so the link opens the standalone post.
  try {
    const url = new URL(embedUrl);
    url.pathname = url.pathname.replace("/embed/", "/");
    url.search = "";
    return url.toString();
  } catch {
    return embedUrl.replace("/embed/", "/");
  }
}

function PostCardSkeleton() {
  return (
    <div
      aria-hidden="true"
      className="flex h-full flex-col overflow-hidden rounded-3xl border border-border/70 bg-surface/40 backdrop-blur-xl"
    >
      <div
        className="flex items-center justify-between"
        style={{
          paddingInline: "clamp(0.875rem, 2.5vw, 1.25rem)",
          paddingTop: "clamp(0.875rem, 2.5vw, 1.25rem)",
        }}
      >
        <div className="h-3 w-24 rounded bg-muted/50 animate-pulse" />
        <div className="h-4 w-4 rounded bg-muted/50 animate-pulse" />
      </div>
      <div
        className="rounded-2xl border border-border/60 bg-background/60 space-y-2"
        style={{
          marginInline: "clamp(0.875rem, 2.5vw, 1.25rem)",
          marginTop: "clamp(0.625rem, 1.5vw, 0.75rem)",
          padding: "clamp(0.75rem, 2vw, 1rem)",
        }}
      >
        <div className="h-2.5 w-20 rounded bg-muted/50 animate-pulse" />
        <div className="h-3 w-full rounded bg-muted/40 animate-pulse" />
        <div className="h-3 w-5/6 rounded bg-muted/40 animate-pulse" />
      </div>
      <div className="mt-auto" style={{ padding: "clamp(0.625rem, 1.5vw, 0.75rem)" }}>
        <div
          className="w-full rounded-2xl border border-border/60 bg-muted/30 animate-pulse"
          style={{ height: "clamp(440px, 50vw, 600px)" }}
        />
      </div>
    </div>
  );
}
